// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import addItemToQuote from "@adl/App/Handlers/CustomQuote/AddItemToQuote";
import getQuoteDetails from "@adl/App/Handlers/CustomQuote/GetCustomQuote";
import { CustomAppState } from "@adl/App/Store/Reducers";
import OrderUploadErrorsModal from "@adl/Overrides/Components/OrderUploadErrorsModal";
import ProductSelector, { ProductSelectorStyles } from "@adl/Overrides/Components/ProductSelector";
import { xScreen } from "@adl/XDL/Utilities";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import translate from "@insite/client-framework/Translate";
import { ProductDto, ProductModel } from "@insite/client-framework/Types/ApiModels";
import OrderUpload from "@insite/content-library/Components/OrderUpload";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import Modal from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quoteId: string;
    sortOrder: number;
}

const mapStateToProps = (state: CustomAppState) => ({
    sortBy: state.xMyCustomQuoteDetails.sortBy,
});

const mapDispatchToProps = {
    addItemToQuote,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface AddItemToQuoteStyles {
    container?: InjectableCss;
    addItems?: ButtonPresentationProps;
    productSelectorStyles?: ProductSelectorStyles;
    uploadItemsButton?: ButtonProps;
}

const addItemToQuoteStyles: AddItemToQuoteStyles = {
    container: {
        css: css`
            display: flex;
            width: 100%;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
            }
        `,
    },
    uploadItemsButton: {
        variant: "tertiary",
        css: css`
            white-space: nowrap;
            margin-top: 15px;
        `,
    },
    productSelectorStyles: {
        qtyGridItem: {
            css: css`
                display: flex;
                justify-content: flex-end;
            `,
        },
        container: {
            css: css`
                &&& {
                    border: none;
                    padding: 0;
                }
            `,
        },
    },
};

const styles = addItemToQuoteStyles;

const AddItemToQuote: FC<Props> = ({ quoteId, addItemToQuote, sortBy, sortOrder }) => {
    const dispatch = useDispatch();
    const toasterContext = useContext(ToasterContext);
    const [addItemsToQuoteIsOpen, setAddItemsToQuoteIsOpen] = useState(false);
    const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);

    const selectProductHandler = (productInfo: ProductInfo, product: ProductModel) => {
        const isAlreadyRelatedProduct = false;

        if (isAlreadyRelatedProduct) {
            toasterContext.addToast({
                body: translate("Item is already added to this quote"),
                messageType: "warning",
            });
            return;
        }

        addItemToQuote({
            customQuoteId: quoteId,
            linesModel: [
                {
                    qty: productInfo.qtyOrdered,
                    productErpNumber: product.productNumber,
                    sortOrder,
                },
            ],
            onSuccess(res: any) {
                dispatch(
                    getQuoteDetails({
                        quoteId,
                        skipPricingAndInventory: false,
                        sortBy,
                    }),
                );
                toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
            },
            onError(error: any) {
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
        });
    };

    const uploadProductsHandler = (products: ProductDto[]) => {
        const linesModel = products.map(o => ({
            productErpNumber: o.erpNumber,
            qty: o.qtyOrdered,
            sortOrder,
        }));
        addItemToQuote({
            customQuoteId: quoteId,
            linesModel,
            onSuccess(res: any) {
                dispatch(
                    getQuoteDetails({
                        quoteId,
                        skipPricingAndInventory: false,
                        sortBy,
                    }),
                );
                uploadItemsModalCloseHandler();
            },
        });
    };

    const uploadItemsClickHandler = () => {
        setUploadItemsModalIsOpen(true);
    };

    const uploadItemsModalCloseHandler = () => {
        setUploadItemsModalIsOpen(false);
    };

    return (
        <StyledWrapper {...styles.container}>
            {addItemsToQuoteIsOpen ? (
                <Button sizeVariant="small" {...styles.addItems} onClick={() => setAddItemsToQuoteIsOpen(true)}>
                    {translate("Add Item To Quote")}
                </Button>
            ) : (
                <>
                    <ProductSelector
                        selectButtonTitle={translate("Add to Quote")}
                        skipInventoryValidation={true}
                        onSelectProduct={selectProductHandler}
                        productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                        productIsUnavailableMessage={siteMessage("Product_NotFound")}
                        extendedStyles={styles.productSelectorStyles}
                    />
                    <Button {...styles.uploadItemsButton} onClick={uploadItemsClickHandler}>
                        {translate("Upload Items")}
                    </Button>
                </>
            )}
            <Modal
                headline={translate("Upload Items To Quote")}
                isOpen={uploadItemsModalIsOpen}
                handleClose={uploadItemsModalCloseHandler}
            >
                <StyledWrapper>
                    <StyledWrapper>
                        <OrderUpload
                            descriptionText={siteMessage("Lists_UploadLinkTooltip")}
                            checkInventory={false}
                            templateUrl="/Excel/ListUploadTemplate.xlsx"
                            onUploadProducts={uploadProductsHandler}
                        />
                        <OrderUploadErrorsModal
                            descriptionText={siteMessage("ListUpload_UploadError")}
                            uploadErrorText={siteMessage("ListUpload_UploadError")}
                            rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                            errorReasons={{
                                [UploadError.ConfigurableProduct]: siteMessage("ListUpload_CannotOrderConfigurable"),
                                [UploadError.StyledProduct]: siteMessage("ListUpload_CannotOrderStyled"),
                                [UploadError.InvalidUnit]: translate("Invalid U/M"),
                                [UploadError.NotFound]: siteMessage("Product_NotFound"),
                            }}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AddItemToQuote);
